// src/components/NFTManage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import CollectionList from './NFT/CollectionList';
import NFTList from './NFT/NFTList';
import NFTDetail from './NFT/NFTDetail';
import NFTRegisterForm from './NFT/NFTRegisterForm';
import { auth } from '../firebase';
import './NFTManage.css';

const NFTManage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [view, setView] = useState('collections'); // 'collections', 'list', 'detail', 'register'
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedNFTId, setSelectedNFTId] = useState(null);
  const [loading, setLoading] = useState(true);

  // 사용자 인증 확인
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setLoading(false);
      } else {
        // 로그인하지 않은 경우 홈으로 리다이렉트
        alert('로그인이 필요합니다.');
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // 관리자 권한 확인
  const isAdmin = user?.email === 'gategil1149@gmail.com' || user?.email === 'worldlandcafe@gmail.com';

  useEffect(() => {
    if (!loading && user && !isAdmin) {
      alert('관리자 권한이 필요합니다.');
      navigate('/');
    }
  }, [loading, user, isAdmin, navigate]);

  // 컬렉션 선택 핸들러
  const handleSelectCollection = (collectionName) => {
    setSelectedCollection(collectionName);
    setView('list');
  };

  // NFT 선택 핸들러
  const handleSelectNFT = (nftId) => {
    setSelectedNFTId(nftId);
    setView('detail');
  };

  // 뒤로가기 핸들러
  const handleBackToCollections = () => {
    setSelectedCollection(null);
    setView('collections');
  };

  const handleBackToList = () => {
    setSelectedNFTId(null);
    setView('list');
  };

  // NFT 등록 성공 핸들러
  const handleRegisterSuccess = () => {
    setView('collections');
  };

  // 관리자 메뉴로 돌아가기
  const handleBackToAdminMenu = () => {
    navigate('/admin-menu');
  };

  // 홈으로 돌아가기
  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="nft-manage-page">
        <div className="nft-manage-loading">
          <div className="spinner"></div>
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  // 컨텐츠 렌더링
  const renderContent = () => {
    switch (view) {
      case 'collections':
        return (
          <CollectionList 
            onSelectCollection={handleSelectCollection}
            onRegisterClick={() => setView('register')}
            onBack={handleBackToAdminMenu}
          />
        );

      case 'list':
        return (
          <NFTList 
            collectionName={selectedCollection}
            onSelectNFT={handleSelectNFT}
            onBack={handleBackToCollections}
            onRegisterClick={() => setView('register')}
          />
        );

      case 'detail':
        return (
          <NFTDetail 
            nftId={selectedNFTId}
            onBack={handleBackToList}
            onDelete={handleBackToList}
            adminEmail={user?.email}
          />
        );

      case 'register':
        return (
          <NFTRegisterForm 
            onSuccess={handleRegisterSuccess}
            onCancel={() => setView('collections')}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="nft-manage-page">
      <header className="nft-manage-header">
        <div className="nft-manage-header-content">
          <button onClick={handleGoHome} className="home-btn">
            <ArrowLeft className="icon" />
            <span>홈으로</span>
          </button>
          <h1>NFT 데이터 관리</h1>
          <div className="user-info">
            <span className="user-email">{user?.email}</span>
            <span className="admin-badge">Admin</span>
          </div>
        </div>
      </header>

      <main className="nft-manage-content">
        {renderContent()}
      </main>

      <footer className="nft-manage-footer">
        <p>© 2025 WorldLand Cafe. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default NFTManage;